import { useEffect, useRef, useState } from 'react';
import { ChevronsUpDown, Check } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface SearchableSelectOption {
  value: string;
  label: string;
  /** Texto extra por el que también se puede buscar, sin mostrarlo en la lista. */
  alias?: string;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  emptyText?: string;
  className?: string;
  id?: string;
  disabled?: boolean;
  /**
   * Si es true, además de elegir de la lista se puede ESCRIBIR un valor nuevo
   * (texto libre). El `value` pasa a ser el texto tal cual. Ideal para conceptos
   * como "tipo de gasto" (combustible, comida, ... o uno propio).
   */
  creatable?: boolean;
}

/**
 * Combobox editable: se escribe DIRECTO en el campo y la lista se filtra al tipear.
 * - Modo normal: solo opciones fijas; si se sale sin elegir una válida, vuelve al valor.
 * - Modo `creatable`: acepta texto libre (lo tipeado ES el valor) y usa las opciones
 *   como sugerencias.
 */
export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Seleccionar...',
  emptyText = 'Sin resultados',
  className,
  id,
  disabled,
  creatable = false
}: SearchableSelectProps) {
  // En creatable el texto mostrado es el propio value; si no, el label de la opción.
  const selectedLabel = creatable ? (value || '') : (options.find((o) => o.value === value)?.label || '');
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(selectedLabel);
  const [dirty, setDirty] = useState(false); // true cuando el usuario tipeó (para filtrar)
  const containerRef = useRef<HTMLDivElement>(null);

  // Mantener el texto en sync con el valor externo cuando NO se está editando.
  useEffect(() => {
    if (!open) setQuery(selectedLabel);
  }, [selectedLabel, open]);

  // Cerrar al hacer click afuera. En creatable se conserva lo tipeado; si no, se revierte.
  useEffect(() => {
    if (!open) return;
    const onDocDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setDirty(false);
        if (!creatable) setQuery(selectedLabel);
      }
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [open, selectedLabel, creatable]);

  // También se busca por `alias`: sirve para lo que no está a la vista pero
  // igual se tipea (la razón social de un proveedor que se muestra por su
  // nombre de fantasía, por ejemplo).
  const filtered = dirty && query.trim()
    ? options.filter((o) => {
        const q = query.trim().toLowerCase();
        return o.label.toLowerCase().includes(q) || (o.alias || '').toLowerCase().includes(q);
      })
    : options;

  const choose = (opt: SearchableSelectOption) => {
    onChange(opt.value);
    setQuery(opt.label);
    setDirty(false);
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <input
          id={id}
          type="text"
          disabled={disabled}
          value={query}
          placeholder={placeholder}
          autoComplete="off"
          onFocus={(e) => { setOpen(true); e.target.select(); }}
          onChange={(e) => {
            const v = e.target.value;
            setQuery(v);
            setDirty(true);
            setOpen(true);
            if (creatable) onChange(v); // texto libre: el value sigue lo tipeado
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              if (!creatable && filtered.length > 0) { e.preventDefault(); choose(filtered[0]); }
              else if (creatable) { e.preventDefault(); setOpen(false); setDirty(false); }
            } else if (e.key === 'Escape') {
              setOpen(false); setDirty(false); if (!creatable) setQuery(selectedLabel);
            }
          }}
          className={cn(
            'flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2 pr-9 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
            className
          )}
        />
        <ChevronsUpDown
          className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 opacity-50"
        />
      </div>
      {open && (
        <div className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-md">
          {filtered.length === 0 ? (
            <div className="px-2 py-1.5 text-sm text-muted-foreground">{emptyText}</div>
          ) : (
            filtered.map((o) => (
              <div
                key={o.value}
                role="option"
                aria-selected={value === o.value}
                onMouseDown={(e) => { e.preventDefault(); choose(o); }}
                className={cn(
                  'flex cursor-pointer items-center rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground',
                  value === o.value && 'bg-accent/50'
                )}
              >
                <Check className={cn('mr-2 h-4 w-4', value === o.value ? 'opacity-100' : 'opacity-0')} />
                {o.label}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
